import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAPI from '../../hooks/useAPI';
import endpoints from '../../Services/endpointUser';
import useDeleteDonatur from '../../hooks/useDeleteDonatur';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ChangePasswordModal from './ChangePasswordModal';

function Profil() {
  const [user, setUser] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedUser = localStorage.getItem('donatur_user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('donatur_user');
      }
    }
  }, []);

  const donaturId = user?.id || user?.donatur?.id;

  const { data, loading, error } = useAPI(
    donaturId ? endpoints.donatur.getById(donaturId) : null
  );
  const { deleteDonatur, loading: deleting, error: deleteError } = useDeleteDonatur();

  const donatur = data?.donatur;
  const isProfileIncomplete = [
    donatur?.nama,
    donatur?.nik,
    donatur?.alamat,
    donatur?.tempat_lahir,
    donatur?.tanggal_lahir,
  ].some((value) => !String(value || '').trim());

  const initials = donatur?.nama
    ? donatur.nama
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase()
    : 'DT';

  const biodataItems = [
    { label: 'NIK', value: donatur?.nik },
    { label: 'Tempat Lahir', value: donatur?.tempat_lahir },
    { label: 'Tanggal Lahir', value: donatur?.tanggal_lahir },
    { label: 'Jenis Kelamin', value: donatur?.jenis_kelamin },
    { label: 'Agama', value: donatur?.agama },
    { label: 'Status Perkawinan', value: donatur?.status_perkawinan },
    { label: 'Pekerjaan', value: donatur?.pekerjaan },
    { label: 'Kewarganegaraan', value: donatur?.kewarganegaraan },
  ];

  const handleEditClick = () => {
    navigate('/profil/edit');
  };

  const handleDelete = async () => {
    if (!donaturId) return;

    const success = await deleteDonatur(donaturId);
    if (success) {
      localStorage.removeItem('donatur_user');
      localStorage.removeItem('donatur_credential');
      window.location.reload();
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-blue-100 p-8 max-w-sm text-center space-y-3 shadow-sm">
          <div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full mx-auto"></div>
          <p className="text-xs font-bold text-blue-900">Silakan login terlebih dahulu.</p>
        </div>
      </div>
    );
  }

  if (loading || !donatur) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl border border-blue-100 p-8 max-w-sm text-center space-y-3 shadow-sm">
          <div className="animate-spin h-8 w-8 border-b-2 border-blue-900 rounded-full mx-auto"></div>
          <p className="text-xs font-bold text-blue-900">Memuat data profil donatur...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5 max-w-md text-center text-xs text-red-700 font-semibold">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-blue-900 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Header Hero Section */}
        <div className="bg-white rounded-3xl border border-blue-100 p-6 md:p-8 shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-blue-950 text-amber-400 font-extrabold text-2xl flex items-center justify-center border border-blue-900 shadow-sm">
                {initials}
              </div>
              <div className="space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-0.5 rounded border border-amber-200">
                  Akun Donatur Terverifikasi
                </span>
                <h1 className="text-2xl font-bold text-blue-900">
                  {donatur?.nama || 'Donatur DT Peduli'}
                </h1>
                <p className="text-xs text-slate-500">
                  Kelola data akun, informasi kontak, dan kata sandi Anda.
                </p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                onClick={handleEditClick}
                className="px-4 py-2.5 rounded-xl bg-blue-900 hover:bg-blue-800 text-white text-xs font-bold shadow-sm transition"
              >
                Edit Profil
              </button>
              <button
                type="button"
                onClick={() => setShowChangePasswordModal(true)}
                className="px-4 py-2.5 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition"
              >
                Ubah Kata Sandi
              </button>
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={deleting}
                className="px-4 py-2.5 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-xs font-bold transition disabled:opacity-50"
              >
                {deleting ? 'Menghapus...' : 'Hapus Akun'}
              </button>
            </div>
          </div>
        </div>

        {deleteError && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 text-xs font-semibold text-red-700">
            {deleteError}
          </div>
        )}

        {isProfileIncomplete && (
          <div className="bg-amber-50/80 border border-amber-200 rounded-2xl p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-0.5">
              <p className="text-xs font-bold text-amber-900">Data Diri Belum Lengkap</p>
              <p className="text-[11px] text-amber-800">
                Lengkapi NIK, alamat, dan tanggal lahir Anda untuk memudahkan verifikasi donasi dan penerbitan bukti laporan.
              </p>
            </div>
            <button
              type="button"
              onClick={handleEditClick}
              className="px-4 py-2 rounded-xl bg-amber-400 hover:bg-amber-300 text-blue-950 text-xs font-bold shadow-sm transition self-start md:self-center flex-shrink-0"
            >
              Lengkapi Sekarang
            </button>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Kolom Kiri: Biodata Pribadi (7 Cols) */}
          <div className="lg:col-span-7 bg-white rounded-3xl border border-blue-100 p-6 md:p-8 shadow-sm space-y-6">
            <div className="pb-3 border-b border-blue-50">
              <h2 className="text-sm font-bold uppercase tracking-wider text-blue-900">Biodata & Identitas</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {biodataItems.map((item) => (
                <div key={item.label} className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-1">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">{item.label}</span>
                  <p className="text-xs font-bold text-blue-900">{item.value || '-'}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Kolom Kanan: Alamat & Kontak (5 Cols) */}
          <div className="lg:col-span-5 space-y-6">
            
            {/* Kartu Alamat Domisili */}
            <div className="bg-white rounded-3xl border border-blue-100 p-6 shadow-sm space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Alamat Domisili</h3>
              <div className="bg-slate-50/60 rounded-2xl p-4 border border-slate-200/70 text-xs text-slate-700 leading-relaxed">
                {donatur?.alamat || 'Belum ada alamat terdaftar.'}
              </div>
            </div>

            {/* Kartu Kontak Akun */}
            <div className="bg-white rounded-3xl border border-blue-100 p-6 shadow-sm space-y-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Informasi Kontak</h3>
              <div className="space-y-2.5">
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Email</span>
                  <p className="text-xs font-bold text-blue-900">{donatur?.email || '-'}</p>
                </div>
                <div className="bg-slate-50/60 rounded-2xl p-3.5 border border-slate-200/70 space-y-0.5">
                  <span className="text-[10px] font-semibold uppercase text-slate-400">Nomor Telepon</span>
                  <p className="text-xs font-bold text-blue-900">{donatur?.no_hp || '-'}</p>
                </div>
              </div>
            </div>

          </div>

        </div>

      </div>

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        title="Hapus Akun"
        message="Apakah Anda yakin ingin menghapus akun donatur ini? Semua data akan dihapus dan tindakan ini tidak dapat dibatalkan."
        confirmText="Hapus"
        cancelText="Batal"
        onConfirm={handleDelete}
        onCancel={handleCancelDelete}
        isLoading={deleting}
      />

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        onClose={() => setShowChangePasswordModal(false)}
      />
    </div>
  );
}

export default Profil;
